import { useContext, useState, useEffect, useCallback, useMemo } from 'react'
import styled from '@emotion/styled'
import { StyledHTMLElement } from 'shop/theme/types'
import { IoIosCalendar as CalendarIcon } from 'react-icons/io'
import { BiEditAlt as EditIcon } from 'react-icons/bi'
import { CurrentLocationState, FulfillmentProps } from '../types'
import { InvalidTimeSlot, ValidStore } from 'shop/components'
import {
  FulfillmentDateContainer,
  FulfillmentDatePicker
} from '../FulfillmentDatePicker'
import { LandingContext } from '../LandingContext'
import TimeSlotSelector from './../TimeSlot'
import {
  calculateStepNumberText,
  getInvalidStoresMessage,
  hasValidStore,
  trackDateChanged,
  trackOrderDayChanged,
  trackStoreChanged,
  trackTimeslotChanged
} from './../utils'
import { isDateAvailable } from './../datePickerUtils'
import {
  useShop,
  useLandingLocation,
  useGoogleApis,
  useSessionStorage
} from 'shop/hooks'

import { useUTMTracker } from '../trackingUtils'
import { StoreSelector } from '../Stores'
import { formatStoreTimeslots } from '../TimeSlot/utils'
import { format, isToday } from 'date-fns'
import { findNextAvailablePreOrderDate } from '../preOrderUtils'
import SelectInput from 'shop/components/Inputs/SelectInput'
import { Divider } from '../../common'
import SelectablePill from '../SelectablePill'
import { SpinnerContainer } from '../commonStyles'
import Spinner from 'shop/components/Loader/Spinner'
import { PseudoFormInput } from '../LocationSelect/FormControls'
import { IoLocationOutline } from 'react-icons/io5'
import { RequiredTag } from './commonStyles'
import { autofillLocation } from '../LocationSelect/utils'

const DeliveryForm = (props: FulfillmentProps) => {
  const {
    stores,
    currentStore,
    currentLocation,
    setCurrentLocation,
    fulfillmentType,
    fulfillmentTime,
    fulfillmentDate,
    setFulfillmentDate,
    setFulfillmentTime,
    setFulfillmentTimeRange,
    setUseCustomerAddress,
    validStores,
    setCurrentStore,
    loadingStores,
    loadingDates,
    setIsExtendedHour,
    storeOutOfRange,
    availableDates,
    sameDayOnly,
    preOrderOnly,
    hasFailedSubmit,
    showFulfillmentOptions,
    inModal,
    defaultLoadCustomerAddress,
    setMapInteracts
  } = useContext(LandingContext)
  const { customerDetails } = useShop()
  const { areGoogleApisLoaded } = useGoogleApis()
  const { getSessionStorageItem: getSavedCurrentLocation } =
    useSessionStorage<CurrentLocationState>('saved-current-location')

  const { handleLocationChange, handleLocationSelect, selectCustomerAddress } =
    useLandingLocation({
      setCurrentLocation,
      setUseCustomerAddress,
      currentLocation,
      setMapInteracts
    })

  useEffect(() => {
    if (preOrderOnly && !sameDayOnly) setHideCalendar(false)
  }, [preOrderOnly, sameDayOnly, currentLocation])

  const datesOrStoresLoading = useMemo(
    () => loadingDates[fulfillmentType] || loadingStores[fulfillmentType],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadingDates[fulfillmentType], loadingStores[fulfillmentType]]
  )

  const [showInvalidTimeslotModal, setShowInvalidTimeslotModal] =
    useState(false)
  const [selectedOrderDay, setSelectedOrderDay] = useState('')
  const [hideCalendar, setHideCalendar] = useState(true)

  useEffect(() => {
    if (isToday(fulfillmentDate) && selectedOrderDay !== 'today') {
      setSelectedOrderDay('today')
    }

    if (!isToday(fulfillmentDate) && selectedOrderDay !== 'future date') {
      setSelectedOrderDay('future date')
    }
  }, [fulfillmentDate, selectedOrderDay])

  /*===== Address =====*/

  const { tracker } = useUTMTracker()

  // Autofill Address - only when address input is empty and user has not cleared it before
  useEffect(() => {
    autofillLocation({
      currentLocation,
      savedCurrentLocation: getSavedCurrentLocation(),
      customerDetails,
      defaultLoadCustomerAddress,
      handleLocationSelect,
      selectCustomerAddress,
      areGoogleApisLoaded
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerDetails?.addresses, areGoogleApisLoaded])

  // Pre-loads address from utmSource
  useEffect(() => {
    if (tracker && tracker.address) {
      handleLocationSelect({ newAddress: tracker.address as string })
    }
  }, []) // eslint-disable-line

  /*===== End Address =====*/

  const handleDateChange = (date: Date | null) => {
    if (datesOrStoresLoading || !date) return
    // do not allow same day selection on the calendar for pre-orders
    if (preOrderOnly && isToday(date)) {
      return
    }

    setFulfillmentDate(date)
    trackDateChanged('landing', date)
    if (isToday(date)) {
      setSelectedOrderDay('today')
    }
    setHideCalendar(true)
  }

  const showDateField = !!currentLocation

  const showStoreList =
    showDateField &&
    !datesOrStoresLoading &&
    !!fulfillmentDate &&
    !!validStores &&
    validStores.length > 0

  const timeSlots = formatStoreTimeslots(
    currentStore?.fulfillmentTimeslots || [],
    fulfillmentDate
  )

  const selectFirstTimeslot = useCallback(() => {
    const timeslot = timeSlots[0]
    setFulfillmentTime(timeslot.value)
    setFulfillmentTimeRange(timeslot.range)
  }, [setFulfillmentTime, setFulfillmentTimeRange, timeSlots])

  useEffect(() => {
    if (timeSlots.length === 1) selectFirstTimeslot()
  }, [timeSlots, selectFirstTimeslot])

  const showTimeslots = showStoreList && !!currentLocation

  const sameDayAndPreOrderAvailable = !preOrderOnly && !sameDayOnly

  const handleSetCurrentStore = (store: ValidStore) => {
    trackStoreChanged(store, 'landing')
    setCurrentStore(store)
  }

  return (
    <>
      <h3>
        {calculateStepNumberText(1, { showFulfillmentOptions })}
        <span>Enter your postcode</span>
        <RequiredTag
          id={`${!!currentLocation?.address ? '' : 'invalid-'}required-tag-postcode`}
          {...{
            isValid: !!currentLocation?.address,
            hasFailed: hasFailedSubmit
          }}
        >
          *required
        </RequiredTag>
      </h3>
      <PseudoFormInput
        onChange={handleLocationChange}
        onSelect={handleLocationSelect}
        searchValue={currentLocation?.address || ''}
        address={currentLocation?.address || ''}
        id={'address-input'}
        customerAddresses={customerDetails?.addresses}
        selectCustomerAddress={selectCustomerAddress}
        inputPlaceholder='Type your postcode here'
        leftIcon={<IoLocationOutline />}
      />
      {!!currentLocation?.address && <Divider />}
      {showDateField && (
        <>
          <h3>
            {calculateStepNumberText(2, { showFulfillmentOptions })}
            <span>Confirm delivery date </span>
            <RequiredTag
              id={`${
                selectedOrderDay === 'today' ||
                (!!fulfillmentDate && hideCalendar)
                  ? ''
                  : 'invalid-'
              }required-tag-date`}
              {...{
                isValid:
                  selectedOrderDay === 'today' ||
                  (!!fulfillmentDate && hideCalendar),
                hasFailed: hasFailedSubmit
              }}
            >
              *required
            </RequiredTag>
          </h3>
          {sameDayAndPreOrderAvailable && (
            <SelectablePill
              options={[
                {
                  title: 'today',
                  type: 'today',
                  selected: selectedOrderDay === 'today',
                  onClick: () => {
                    handleDateChange(new Date())
                    setSelectedOrderDay('today')
                    trackOrderDayChanged('today', 'landing')
                  }
                },
                {
                  title: 'future date',
                  type: 'future-date',
                  selected: selectedOrderDay === 'future date',
                  onClick: () => {
                    const nextDate = findNextAvailablePreOrderDate(
                      stores,
                      fulfillmentType,
                      availableDates
                    )
                    handleDateChange(nextDate)
                    setSelectedOrderDay('future date')
                    setHideCalendar(false)
                    trackOrderDayChanged('future date', 'landing')
                  }
                }
              ]}
            />
          )}
          {sameDayOnly && selectedOrderDay === 'today' && (
            <>
              <SelectInput
                testId='change-calendar-date'
                disabled={true}
                onClick={() => {}}
                ToSelectIcon={CalendarIcon}
                HasSelectedIcon={EditIcon}
                hasSelected={!!fulfillmentDate}
                textFallback={`${format(fulfillmentDate, 'EEEE')} ${format(
                  fulfillmentDate,
                  'dd/MM/yyyy'
                )}`}
              />
            </>
          )}
          {selectedOrderDay === 'future date' && (
            <>
              {hideCalendar && (
                <>
                  <SelectInput
                    testId='change-calendar-date'
                    onClick={() => setHideCalendar(false)}
                    ToSelectIcon={CalendarIcon}
                    HasSelectedIcon={EditIcon}
                    hasSelected={!!fulfillmentDate}
                    textFallback={`${format(fulfillmentDate, 'EEEE')} ${format(
                      fulfillmentDate,
                      'dd/MM/yyyy'
                    )}`}
                  />
                </>
              )}

              {!hideCalendar && (
                <FulfillmentDateSection>
                  <FulfillmentDateContainer>
                    <FulfillmentDatePicker
                      selected={fulfillmentDate}
                      onChange={handleDateChange}
                      onSelect={handleDateChange}
                      minDate={new Date()}
                      filterDate={(date: Date) =>
                        isDateAvailable(availableDates, date, 'delivery')
                      }
                      placeholderText='dd/mm/yyyy'
                      autoFocus={fulfillmentDate === null}
                      dateFormat='dd/MM/yyyy'
                      disabled={datesOrStoresLoading}
                    />
                  </FulfillmentDateContainer>
                </FulfillmentDateSection>
              )}
            </>
          )}
          <Divider />
        </>
      )}
      {showStoreList && (
        <>
          <h3>
            {calculateStepNumberText(3, { showFulfillmentOptions })}
            <span>Pick a store near you</span>
            <RequiredTag
              id={`${!!currentStore ? '' : 'invalid-'}required-tag-store`}
              {...{ isValid: !!currentStore, hasFailed: hasFailedSubmit }}
            >
              *required
            </RequiredTag>
          </h3>
          <StoreSelector
            validStores={validStores}
            setCurrentStore={handleSetCurrentStore}
            currentStore={currentStore}
            inModal={inModal}
          />
          <Divider />
        </>
      )}

      {currentLocation && !datesOrStoresLoading && validStores.length === 0 && (
        <Message>
          {getInvalidStoresMessage(
            fulfillmentDate,
            storeOutOfRange,
            !!hasValidStore(stores, 'pickup')
          )}
        </Message>
      )}
      {showTimeslots && (
        <>
          <h3>
            {calculateStepNumberText(4, { showFulfillmentOptions })}
            <span>Choose delivery timeslot</span>
            <RequiredTag
              id={`${!!fulfillmentTime ? '' : 'invalid-'}required-tag-time`}
              {...{ isValid: !!fulfillmentTime, hasFailed: hasFailedSubmit }}
            >
              *required
            </RequiredTag>
          </h3>
          <TimeSlotContainer>
            <TimeSlotSelector
              timeSlots={timeSlots}
              value={fulfillmentTime}
              handleUpdate={(time, range, extended) => {
                setFulfillmentTime(time)
                setFulfillmentTimeRange(range)
                setIsExtendedHour(extended)
                if (range) {
                  trackTimeslotChanged(range, 'landing')
                }
              }}
              inModal={inModal}
            />
          </TimeSlotContainer>
        </>
      )}
      {datesOrStoresLoading && (
        <SpinnerContainer>
          <Spinner size='40px' />
        </SpinnerContainer>
      )}
      <InvalidTimeSlot
        showModal={showInvalidTimeslotModal}
        onClose={() => setShowInvalidTimeslotModal(false)}
      />
    </>
  )
}

const Message = styled.p<StyledHTMLElement>(({ theme }) => ({
  margin: '32px 0 8px',
  fontSize: theme.fontSizes[2],
  textTransform: 'none',
  color: theme.colors.textMute
}))

const FulfillmentDateSection = styled.div<StyledHTMLElement>(() => ({
  marginBottom: 0,
  '& > h3': {
    display: 'block'
  }
}))

const TimeSlotContainer = styled.div<StyledHTMLElement>(() => ({
  marginBottom: '36px'
}))

export default DeliveryForm
